import { FC } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { Tip, TooltipCard } from '@components';
import { DI_TOKENS } from '@constants';
import { useDirection, useInjection } from '@hooks';
import { AssessmentDomainStatus } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { ArrowLeftIcon, ArrowRightIcon, ChecksIcon, HouseIcon, NotePencilIcon, PlayCircleIcon } from '@phosphor-icons/react';
import { AssessmentWorkflowStore } from '@stores';
import { NoFlipBox } from '@utils';

/**
 * Props are limited to handlers + controlled state. Derived flags
 * (`isNavigationDisabled`, `isDomainInProgress`, `isLastDomain`, etc.) are
 * read from `AssessmentWorkflowStore.currentDomainFlags` so the page doesn't
 * thread them through.
 */
export interface DomainNavigationProps {
  onBack: () => void;
  onResume: () => void;
  onSaveAndNext: () => void;
  onNext?: () => void;
  onResumeLater?: () => void;
  onGoToSummary?: () => void;
  onEdit?: () => void;
  onNoEvidenceChange?: (checked: boolean) => void;
  /** Controlled checkbox state — owned by `useDomainEditor`. */
  noEvidenceChecked?: boolean;
  /** Controlled applicability state — owned by `useDomainEditor`. */
  isApplicable?: boolean;
  /** Controlled "click Edit to start" tooltip — owned by `useEditPromptTooltip`. */
  editPromptTooltipOpen?: boolean;
  onEditPromptTooltipOpen?: () => void;
  onEditPromptTooltipClose?: () => void;
}

export const DomainNavigation: FC<DomainNavigationProps> = observer(
  ({
    onBack,
    onResume,
    onSaveAndNext,
    onNext,
    onResumeLater,
    onNoEvidenceChange,
    noEvidenceChecked = false,
    onEdit,
    onGoToSummary,
    isApplicable,
    editPromptTooltipOpen,
    onEditPromptTooltipOpen,
    onEditPromptTooltipClose,
  }) => {
    const { t } = useTrans();
    const { dir, isRtl } = useDirection();
    const { enqueueSnackbar } = useSnackbar();

    const store = useInjection<AssessmentWorkflowStore>(
      DI_TOKENS.AssessmentWorkflowStore,
    );
    const {
      isDomainFieldsDisabled,
      isDomainCompleted,
      isFirstDomain,
      isLastDomain,
      isDomainInProgress,
      uploadedDocumentsCount,
      allDomainsCompleted,
      nextDomainStatus,
      currentDomainStatus,
      isSaveAndNextDisabled,
    } = store.currentDomainFlags;
    const isNavigationDisabled = store.isNavigationDisabled;

    // Handler for "No evidence" checkbox with validation
    const handleNoEvidenceChange = (checked: boolean) => {
      if (checked && uploadedDocumentsCount > 0) {
        // Prevent checking if there are uploaded documents
        enqueueSnackbar(t('app.assessments.workflow.removeDocumentsFirst'), {
          variant: 'warning',
        });
        return;
      }
      onNoEvidenceChange?.(checked);
    };

    // Determine if "No evidence" checkbox should be disabled
    const isNoEvidenceCheckboxDisabled =
      isDomainFieldsDisabled || !isDomainInProgress || !isApplicable;

    return (
      <NoFlipBox
        mode={dir}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1, md: 1.5 },
          flexWrap: 'wrap',
          justifyContent: isFirstDomain ? 'flex-end' : 'space-between',
          minWidth: 0,
        }}
      >
        {/* Back Button - Left - Only show if not first domain */}
        {!isFirstDomain && (
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isNavigationDisabled}
            sx={{
              minWidth: 'auto',
              order: 1,
              textTransform: 'none',
              padding: '6px 12px',
              borderColor: 'transparent',
              color: 'text.primary',
              backgroundColor: 'grey.200',
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              '&:hover': {
                borderColor: 'transparent',
                backgroundColor: 'grey.100',
              },
              gap: 1,
              width: { xs: '100%', md: 'auto' },
              maxWidth: '100%',
            }}
          >
            {isRtl ? <ArrowRightIcon size={18} /> : <ArrowLeftIcon size={18} />}
            <Trans i18nKey={'app.common.back'} />
          </Button>
        )}

        {/* Right Section - Save/Edit and Resume Later */}
        <NoFlipBox
          mode={dir}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: { xs: 'stretch', md: 'flex-end' },
            flexWrap: 'wrap',
            gap: { xs: 1, md: 1.5 },
            order: 3,
            width: { xs: '100%', md: 'auto' },
            minWidth: 0,
          }}
        >
          {/* Checkbox - Only for required domains */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 0.5,
              width: { xs: '100%', md: 'auto' },
              minWidth: 0,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={noEvidenceChecked}
                  onChange={(e) => handleNoEvidenceChange(e.target.checked)}
                  disabled={isNoEvidenceCheckboxDisabled}
                  size="small"
                />
              }
              label={
                <Typography
                  variant="textSM_regular"
                  sx={{
                    color: isNoEvidenceCheckboxDisabled ? 'black.200' : 'text.primary',
                    fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  <Trans i18nKey={'assessment.workflow.domain.markAsNoEvidenceAvailable'} />
                </Typography>
              }
              labelPlacement="end"
              sx={{
                margin: 0,
                minWidth: 0,
                width: { xs: '100%', md: 'auto' },
                alignItems: { xs: 'flex-start', md: 'center' },
                '& .MuiFormControlLabel-label': {
                  minWidth: 0,
                },
              }}
            />
            <Tip title={<Trans i18nKey={'assessment.workflow.domain.noEvidenceTooltip'} />} />
          </Box>

          {/* Resume Later Button */}
          {onResumeLater && (
            <Button
              variant="text"
              onClick={onResumeLater}
              disabled={isNavigationDisabled}
              sx={{
                minWidth: 'auto',
                textTransform: 'none',
                color: 'text.primary',
                backgroundColor: 'grey.200',
                px: { xs: 1.75, sm: 2, md: 2.25 },
                py: { xs: 0.5, sm: 0.625, md: 0.625 },
                border: '1px solid transparent',
                boxShadow: 'none',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                width: { xs: '100%', md: 'auto' },
                maxWidth: '100%',
                whiteSpace: 'normal',
                '&:hover': {
                  backgroundColor: 'grey.200',
                },
              }}
            >
              <Trans i18nKey={'assessment.workflow.domain.resumeLater'} />
            </Button>
          )}

          {/* Edit Button - Only show when domain is COMPLETED */}
          {isDomainCompleted && onEdit && (
            <TooltipCard
              variant="warning"
              placement="top"
              open={editPromptTooltipOpen}
              onOpen={onEditPromptTooltipOpen}
              onClose={onEditPromptTooltipClose}
              description={t('assessment.workflow.domain.editToStart')}
            >
              <Button
                variant="contained"
                onClick={onEdit}
                disabled={isNavigationDisabled}
                sx={{
                  minWidth: '36px',
                  width: '36px',
                  height: '36px',
                  p: 0,
                  backgroundColor: 'primary.main', // Using the "old blue code" from the border
                  color: '#F9FAFB', // Very light grey (nearly white) for clarity
                  boxShadow: 'none',
                  borderRadius: '10px',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 12px rgba(0, 99, 179, 0.3)',
                  },
                }}
              >
                <NotePencilIcon size={20} weight="regular" />
              </Button>
            </TooltipCard>
          )}

          {/* Save and Next / Finish Assessment Button - Only show when domain is NOT completed */}
          {(!isDomainCompleted || isLastDomain) && (
            <Button
              variant="contained"
              onClick={onSaveAndNext}
              disabled={isNavigationDisabled || isSaveAndNextDisabled}
              sx={{
                minWidth: 'auto',
                textTransform: 'none',
                px: { xs: 1.75, sm: 2, md: 2.25 },
                py: { xs: 0.5, sm: 0.625, md: 0.625 },
                // Use green styling for last domain (finish), black for regular save & next
                backgroundColor: isLastDomain ? 'green.700' : 'text.primary',
                color: 'background.paper',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: isLastDomain ? 500 : 400,
                width: { xs: '100%', md: 'auto' },
                maxWidth: '100%',
                whiteSpace: 'normal',
                gap: 1,
                boxShadow: isLastDomain ? '0 2px 4px rgba(27, 158, 92, 0.2)' : 'none',
                // transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isLastDomain ? 'green.800' : 'text.primary',
                  opacity: isLastDomain ? 1 : 0.9,
                  transform: isLastDomain ? 'translateY(-1px)' : 'none',
                  boxShadow: isLastDomain ? '0 4px 12px rgba(27, 158, 92, 0.3)' : 'none',
                },
              }}
            >
              <ChecksIcon size={18} />
              {isLastDomain ? (
                <Trans i18nKey={'assessment.workflow.domain.finishAssessment'} />
              ) : (
                <Trans i18nKey={'assessment.workflow.domain.saveAndNext'} />
              )}
            </Button>
          )}

          {/* Go to Summary Button - shown when all domains are completed and not on the last domain */}
          {!isLastDomain && allDomainsCompleted && onGoToSummary && (
            <Button
              variant="contained"
              onClick={onGoToSummary}
              disabled={isNavigationDisabled}
              sx={{
                minWidth: 'auto',
                textTransform: 'none',
                px: { xs: 1.75, sm: 2, md: 2.25 },
                py: { xs: 0.5, sm: 0.625, md: 0.625 },
                backgroundColor: 'green.700',
                color: 'background.paper',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
                width: { xs: '100%', md: 'auto' },
                maxWidth: '100%',
                whiteSpace: 'normal',
                gap: 1,
                boxShadow: '0 2px 4px rgba(27, 158, 92, 0.2)',
                '&:hover': {
                  backgroundColor: 'green.800',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(27, 158, 92, 0.3)',
                },
              }}
            >
              <HouseIcon size={18} />
              <Trans i18nKey={'assessments.workflow.domain.goToSummary'} />
            </Button>
          )}

          {/* Resume button for not-in-progress domains (before last domain) */}
          {onResume && !isDomainInProgress && !allDomainsCompleted && !isLastDomain && (
            <Button
              variant="contained"
              onClick={onResume}
              disabled={isNavigationDisabled}
              startIcon={<PlayCircleIcon size={18} weight="fill" />}
              sx={{
                minWidth: 'auto',
                textTransform: 'none',
                px: { xs: 1.75, sm: 2, md: 2.25 },
                py: { xs: 0.5, sm: 0.625, md: 0.625 },
                backgroundColor: 'green.700',
                color: 'background.paper',
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                fontWeight: 500,
                width: { xs: '100%', md: 'auto' },
                maxWidth: '100%',
                whiteSpace: 'normal',
                gap: 0.75,
                boxShadow: '0 2px 4px rgba(27, 158, 92, 0.2)',
                // transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'green.800',
                  // transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(27, 158, 92, 0.3)',
                },
              }}
            >
              <Trans i18nKey={'assessment.workflow.domain.resume'} />
            </Button>
          )}

          {/* Next Button - Only show when:
              - Domain is NOT in progress
              - Not last domain
              - Current domain is NOT not_started (show Resume instead)
              - Next domain is NOT not_started
          */}
          {!isDomainInProgress &&
            !isLastDomain &&
            onNext &&
            currentDomainStatus !== AssessmentDomainStatus.NOT_STARTED &&
            nextDomainStatus !== AssessmentDomainStatus.NOT_STARTED && (
              <Button
                variant="outlined"
                onClick={onNext}
                disabled={isNavigationDisabled}
                sx={{
                  minWidth: 'auto',
                  textTransform: 'none',
                  padding: '6px 12px',
                  borderColor: 'transparent',
                  color: 'text.primary',
                  backgroundColor: 'grey.200',
                  fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                  width: { xs: '100%', md: 'auto' },
                  maxWidth: '100%',
                  whiteSpace: 'normal',
                  '&:hover': {
                    borderColor: 'transparent',
                    backgroundColor: 'grey.100',
                  },
                  gap: 1,
                }}
              >
                <Trans i18nKey={'assessment.workflow.domain.next'} />
                {isRtl ? <ArrowLeftIcon size={18} /> : <ArrowRightIcon size={18} />}
              </Button>
            )}
        </NoFlipBox>
      </NoFlipBox>
    );
  },
);
